// doctor-frontend/src/clinical/visitComplaintRegistry.js
// Structured chief-complaint registry for CMFM Visit creation.
// Complaint entries are atomic symptoms/problems. Pathways are derived separately.

export const DURATION_UNITS = [
  'Minutes',
  'Hours',
  'Days',
  'Weeks',
  'Months',
  'Years',
];

const C = (code, label, complaintGroup, defaultPathway, modules = [], aliases = []) => ({
  code,
  label,
  complaintGroup,
  defaultPathway,
  modules,
  aliases,
});

export const COMPLAINT_REGISTRY = [
  // General / constitutional
  C('fever', 'Fever', 'Fever / Infection', 'Acute Febrile / Infective Illness', ['fever-infection'], ['pyrexia', 'high temperature']),
  C('chills-rigors', 'Chills / rigors', 'Fever / Infection', 'Acute Febrile / Infective Illness', ['fever-infection']),
  C('weakness-fatigue', 'General weakness / fatigue', 'General Weakness / Fatigue', 'General Symptom Assessment', ['constitutional']),
  C('loss-appetite', 'Loss of appetite', 'General Weakness / Fatigue', 'General Symptom Assessment', ['constitutional','nutrition']),
  C('weight-loss', 'Unintentional weight loss', 'General Weakness / Fatigue', 'Chronic Symptom Assessment', ['constitutional','nutrition']),
  C('weight-gain', 'Unexplained weight gain', 'General Weakness / Fatigue', 'General Symptom Assessment', ['constitutional','ncd']),
  C('night-sweats', 'Night sweats', 'Fever / Infection', 'Chronic Infective / Constitutional Assessment', ['fever-infection','constitutional']),
  C('body-ache', 'Body ache / myalgia', 'Pain / Musculoskeletal', 'Acute Symptom Assessment', ['musculoskeletal']),

  // Respiratory / ENT
  C('cough', 'Cough', 'Respiratory Symptoms', 'Respiratory Symptom Pathway', ['respiratory'], ['khansi']),
  C('cold-coryza', 'Cold / coryza / runny nose', 'Respiratory Symptoms', 'Upper Respiratory Symptom Pathway', ['respiratory','ent'], ['cold', 'runny nose', 'coryza']),
  C('sore-throat', 'Sore throat', 'Respiratory Symptoms', 'Upper Respiratory Symptom Pathway', ['respiratory','ent']),
  C('sputum', 'Sputum / expectoration', 'Respiratory Symptoms', 'Respiratory Symptom Pathway', ['respiratory']),
  C('breathlessness', 'Breathlessness / shortness of breath', 'Respiratory Symptoms', 'Respiratory Symptom Pathway', ['respiratory','cardiovascular'], ['dyspnoea', 'dyspnea', 'shortness of breath']),
  C('wheeze', 'Wheeze', 'Respiratory Symptoms', 'Respiratory Symptom Pathway', ['respiratory']),
  C('hemoptysis', 'Coughing blood / haemoptysis', 'Respiratory Symptoms', 'Respiratory Red-Flag Pathway', ['respiratory','red-flag'], ['hemoptysis', 'haemoptysis']),
  C('nasal-block', 'Nasal blockage', 'Respiratory Symptoms', 'ENT Symptom Pathway', ['ent']),
  C('ear-pain', 'Ear pain', 'Respiratory Symptoms', 'ENT Symptom Pathway', ['ent']),
  C('ear-discharge', 'Ear discharge', 'Respiratory Symptoms', 'ENT Symptom Pathway', ['ent']),
  C('hearing-difficulty', 'Hearing difficulty / tinnitus', 'Neurological Symptoms', 'ENT / Hearing Assessment', ['ent','occupational']),
  C('hoarseness', 'Hoarseness / voice change', 'Respiratory Symptoms', 'ENT Symptom Pathway', ['ent']),

  // Cardiovascular
  C('chest-pain', 'Chest pain', 'Chest Pain / Cardiovascular Symptoms', 'Chest Pain / Cardiovascular Pathway', ['cardiovascular','red-flag']),
  C('palpitations', 'Palpitations', 'Chest Pain / Cardiovascular Symptoms', 'Cardiovascular Symptom Pathway', ['cardiovascular']),
  C('syncope', 'Fainting / syncope', 'Chest Pain / Cardiovascular Symptoms', 'Syncope / Cardiovascular Pathway', ['cardiovascular','neurological','red-flag']),
  C('pedal-edema', 'Swelling of feet / legs', 'Chest Pain / Cardiovascular Symptoms', 'Cardiovascular / Systemic Edema Pathway', ['cardiovascular','renal']),
  C('orthopnea', 'Breathlessness on lying down / orthopnoea', 'Chest Pain / Cardiovascular Symptoms', 'Cardiovascular Symptom Pathway', ['cardiovascular','respiratory']),

  // Gastrointestinal
  C('abdominal-pain', 'Pain abdomen / abdominal pain', 'Gastrointestinal Symptoms', 'Gastrointestinal Symptom Pathway', ['gastrointestinal'], ['abd pain', 'stomach pain', 'pain abdomen']),
  C('nausea', 'Nausea', 'Gastrointestinal Symptoms', 'Gastrointestinal Symptom Pathway', ['gastrointestinal']),
  C('vomiting', 'Vomiting', 'Gastrointestinal Symptoms', 'Gastrointestinal Symptom Pathway', ['gastrointestinal']),
  C('diarrhea', 'Loose stools / diarrhoea', 'Gastrointestinal Symptoms', 'Diarrhoeal Illness Pathway', ['gastrointestinal','dehydration'], ['diarrhea', 'loose motion', 'loose stools']),
  C('constipation', 'Constipation', 'Gastrointestinal Symptoms', 'Gastrointestinal Symptom Pathway', ['gastrointestinal']),
  C('dyspepsia', 'Acidity / dyspepsia / heartburn', 'Gastrointestinal Symptoms', 'Upper GI Symptom Pathway', ['gastrointestinal']),
  C('gi-bleeding', 'Blood in vomit / stool / black stool', 'Gastrointestinal Symptoms', 'GI Bleeding Pathway', ['gastrointestinal','red-flag']),
  C('jaundice', 'Jaundice / yellow discoloration', 'Gastrointestinal Symptoms', 'Hepatobiliary Symptom Pathway', ['gastrointestinal']),
  C('abdominal-distension', 'Abdominal distension', 'Gastrointestinal Symptoms', 'Gastrointestinal Symptom Pathway', ['gastrointestinal']),

  // Neurological
  C('headache', 'Headache', 'Neurological Symptoms', 'Neurological Symptom Pathway', ['neurological']),
  C('dizziness', 'Dizziness / vertigo', 'Neurological Symptoms', 'Neurological Symptom Pathway', ['neurological']),
  C('seizure', 'Seizure / convulsion', 'Neurological Symptoms', 'Neurological Red-Flag Pathway', ['neurological','red-flag']),
  C('altered-sensorium', 'Altered sensorium / confusion', 'Neurological Symptoms', 'Neurological Red-Flag Pathway', ['neurological','red-flag']),
  C('weakness-focal', 'Focal weakness / paralysis', 'Neurological Symptoms', 'Neurological Red-Flag Pathway', ['neurological','red-flag']),
  C('numbness', 'Numbness / tingling', 'Neurological Symptoms', 'Neurological Symptom Pathway', ['neurological']),
  C('tremor', 'Tremor / abnormal movement', 'Neurological Symptoms', 'Neurological Symptom Pathway', ['neurological']),
  C('memory', 'Memory / cognitive concern', 'Neurological Symptoms', 'Cognitive / Geriatric Assessment', ['neurological','geriatric']),

  // Musculoskeletal / pain
  C('back-pain', 'Back pain', 'Pain / Musculoskeletal', 'Musculoskeletal Symptom Pathway', ['musculoskeletal','occupational']),
  C('neck-pain', 'Neck pain', 'Pain / Musculoskeletal', 'Musculoskeletal Symptom Pathway', ['musculoskeletal','occupational']),
  C('joint-pain', 'Joint pain', 'Pain / Musculoskeletal', 'Musculoskeletal Symptom Pathway', ['musculoskeletal']),
  C('joint-swelling', 'Joint swelling', 'Pain / Musculoskeletal', 'Musculoskeletal Symptom Pathway', ['musculoskeletal']),
  C('limb-pain', 'Limb pain', 'Pain / Musculoskeletal', 'Musculoskeletal Symptom Pathway', ['musculoskeletal','occupational']),
  C('muscle-cramp', 'Muscle cramps', 'Pain / Musculoskeletal', 'Musculoskeletal Symptom Pathway', ['musculoskeletal']),

  // Urinary / renal
  C('dysuria', 'Burning / pain during urination', 'Gastrointestinal Symptoms', 'Urinary Symptom Pathway', ['urinary']),
  C('frequency-urine', 'Increased urinary frequency', 'Gastrointestinal Symptoms', 'Urinary / Metabolic Symptom Pathway', ['urinary','ncd']),
  C('hematuria', 'Blood in urine', 'Gastrointestinal Symptoms', 'Urinary Red-Flag Pathway', ['urinary','red-flag']),
  C('flank-pain', 'Flank / loin pain', 'Gastrointestinal Symptoms', 'Urinary Symptom Pathway', ['urinary']),
  C('reduced-urine', 'Reduced urine output', 'General Weakness / Fatigue', 'Renal / Dehydration Pathway', ['urinary','dehydration']),

  // Skin
  C('rash', 'Skin rash', 'Skin / Allergy', 'Dermatology / Allergy Pathway', ['dermatology']),
  C('itching', 'Itching / pruritus', 'Skin / Allergy', 'Dermatology / Allergy Pathway', ['dermatology','occupational']),
  C('skin-ulcer', 'Skin ulcer / non-healing wound', 'Skin / Allergy', 'Wound / Dermatology Pathway', ['dermatology','wound']),
  C('swelling', 'Localized swelling / lump', 'Skin / Allergy', 'Localized Swelling Assessment', ['dermatology']),
  C('allergic-reaction', 'Allergic reaction / urticaria', 'Skin / Allergy', 'Allergy Pathway', ['dermatology','red-flag']),

  // Eye
  C('red-eye', 'Red eye', 'Other General OPD', 'Eye Symptom Pathway', ['eye','occupational']),
  C('eye-pain', 'Eye pain', 'Other General OPD', 'Eye Symptom Pathway', ['eye','red-flag']),
  C('vision-problem', 'Reduced / blurred vision', 'Other General OPD', 'Eye Symptom Pathway', ['eye','red-flag']),
  C('eye-discharge', 'Eye discharge / watering', 'Other General OPD', 'Eye Symptom Pathway', ['eye']),

  // Reproductive / maternal
  C('missed-period', 'Missed period / amenorrhoea', 'Other General OPD', 'Reproductive Health Pathway', ['reproductive']),
  C('vaginal-bleeding', 'Vaginal bleeding', 'Other General OPD', 'Reproductive / Obstetric Pathway', ['reproductive','maternal','red-flag']),
  C('vaginal-discharge', 'Vaginal discharge', 'Other General OPD', 'Reproductive / STI Pathway', ['reproductive']),
  C('pelvic-pain', 'Pelvic / lower abdominal pain', 'Gastrointestinal Symptoms', 'Reproductive / GI Pathway', ['reproductive','gastrointestinal']),
  C('labour-pain', 'Labour pain / contractions', 'Other General OPD', 'Pregnancy Emergency Assessment', ['maternal','red-flag']),
  C('leaking-pv', 'Leaking per vaginum', 'Other General OPD', 'Pregnancy Emergency Assessment', ['maternal','red-flag']),
  C('reduced-fetal-movement', 'Reduced fetal movements', 'Other General OPD', 'Pregnancy Emergency Assessment', ['maternal','red-flag']),
  C('anc-routine', 'Routine antenatal visit', 'Routine ANC', 'ANC Registration / Follow-up', ['maternal']),
  C('pnc-routine', 'Routine postnatal visit', 'Routine Postnatal Review', 'Postnatal Mother Review', ['maternal','newborn']),

  // Newborn / child
  C('poor-feeding', 'Poor feeding / not feeding', 'Poor Feeding / Not Feeding', 'Sick Newborn / Young Infant Pathway', ['newborn','paediatric','red-flag']),
  C('child-cough', 'Cough / breathing problem in child', 'Cough / Respiratory', 'IMNCI / Sick Child Pathway', ['paediatric','respiratory']),
  C('child-diarrhea', 'Diarrhoea / vomiting in child', 'Diarrhea / Vomiting', 'IMNCI / Sick Child Pathway', ['paediatric','gastrointestinal','dehydration']),
  C('child-fever', 'Fever in child', 'Fever / Infection', 'IMNCI / Sick Child Pathway', ['paediatric','fever-infection']),
  C('child-growth', 'Growth / nutrition concern', 'Growth / Nutrition / Malnutrition', 'Growth & Nutrition Pathway', ['paediatric','nutrition']),
  C('development', 'Developmental concern', 'Developmental Concern', 'Developmental Assessment Pathway', ['paediatric','development']),
  C('immunization', 'Immunization / vaccine-related visit', 'Immunization Related', 'Immunization / Child Review', ['paediatric','immunization']),
  C('newborn-jaundice', 'Jaundice in newborn', 'Jaundice', 'Neonatal Jaundice Pathway', ['newborn']),
  C('newborn-routine', 'Routine newborn / young-infant review', 'Birth Follow-up / Routine Newborn Review', 'Birth Follow-up / Essential Newborn Care', ['newborn']),

  // Mental health / behavioural
  C('anxiety', 'Anxiety / excessive worry', 'Mental Health / Psychosocial Concern', 'Mental Health / Psychosocial Pathway', ['mental-health']),
  C('low-mood', 'Low mood / depression symptoms', 'Mental Health / Psychosocial Concern', 'Mental Health / Psychosocial Pathway', ['mental-health']),
  C('sleep', 'Sleep disturbance', 'Mental Health / Psychosocial Concern', 'Mental Health / Psychosocial Pathway', ['mental-health']),
  C('self-harm', 'Self-harm / suicidal thoughts', 'Mental Health / Psychosocial Concern', 'Mental Health Urgent Assessment', ['mental-health','red-flag']),
  C('substance', 'Substance-use concern', 'Mental Health / Psychosocial Concern', 'Substance-use Assessment Pathway', ['mental-health','substance']),
  C('violence-safety', 'Violence / abuse / safety concern', 'Mental Health / Psychosocial Concern', 'Safety / Psychosocial Assessment', ['mental-health','red-flag']),

  // Injury / emergency
  C('injury', 'Injury / trauma', 'Injury / Trauma', 'Injury / Trauma Pathway', ['injury','red-flag']),
  C('wound', 'Wound / laceration', 'Injury / Trauma', 'Wound / Injury Procedure Pathway', ['injury','wound']),
  C('burn', 'Burn / scald', 'Injury / Trauma', 'Burn / Injury Pathway', ['injury']),
  C('bite', 'Animal / insect bite', 'Injury / Trauma', 'Bite / Exposure Pathway', ['injury','infection']),
  C('poisoning', 'Suspected poisoning / toxic exposure', 'Injury / Trauma', 'Poisoning / Toxic Exposure Pathway', ['toxicology','occupational','red-flag']),
  C('heat-illness', 'Heat-related symptoms', 'Other General OPD', 'Heat Illness Pathway', ['heat','occupational','red-flag']),

  // NCD / follow-up
  C('hypertension-followup', 'Hypertension follow-up / high BP', 'Hypertension', 'Hypertension Screening / Follow-up', ['ncd','cardiovascular']),
  C('diabetes-followup', 'Diabetes follow-up / high blood sugar', 'Diabetes', 'Diabetes Screening / Follow-up', ['ncd','metabolic']),
  C('combined-ncd', 'Combined NCD follow-up', 'Combined NCD Follow-up', 'Combined NCD Follow-up', ['ncd','cardiovascular','metabolic']),
  C('ncd-risk', 'NCD risk assessment / screening', 'Risk Assessment / Counseling', 'NCD Risk Assessment / Counseling', ['ncd']),

  // Occupational / work-related
  C('work-related-symptom', 'Symptoms possibly related to work', 'Occupational Exposure', 'Occupational Exposure Review', ['occupational']),
  C('work-dust-respiratory', 'Respiratory symptoms with dust / fume exposure', 'Respiratory Occupational', 'Respiratory Occupational Health', ['occupational','respiratory']),
  C('work-skin', 'Skin symptoms possibly related to work', 'Occupational Exposure', 'Occupational Exposure Review', ['occupational','dermatology']),
  C('work-hearing', 'Hearing symptoms with workplace noise', 'Occupational Exposure', 'Occupational Exposure Review', ['occupational','ent']),
  C('work-musculoskeletal', 'Pain / strain possibly related to work', 'Occupational Exposure', 'Occupational Exposure Review', ['occupational','musculoskeletal']),
  C('work-injury', 'Workplace injury / exposure incident', 'Injury / Trauma', 'Occupational Injury Assessment', ['occupational','injury']),

  // Procedure / counselling / other
  C('procedure-followup', 'Procedure / dressing follow-up', 'Dressing / Follow-up Procedure', 'Procedure Pathway', ['procedure']),
  C('counseling', 'Counselling / health advice visit', 'Counseling', 'Counseling Pathway', ['counseling']),
  C('medication-review', 'Medication review / adverse effect concern', 'Other General OPD', 'Medication Review Pathway', ['medication-safety']),
  C('followup-no-new', 'Follow-up / review with no new complaint', 'Other General OPD', 'Review / Follow-up Pathway', ['follow-up']),
  C('other', 'Other complaint', 'Other General OPD', 'General Symptom Assessment', ['general']),
];

export const COMPLAINT_OPTIONS = COMPLAINT_REGISTRY.map((item) => item.label);

const normalize = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[–—]/g, '-')
  .replace(/\s+/g, ' ');

export function findComplaintDefinition(value) {
  const key = normalize(value);
  if (!key) return null;
  return COMPLAINT_REGISTRY.find((item) => (
    normalize(item.label) === key ||
    normalize(item.code) === key ||
    (item.aliases || []).some((alias) => normalize(alias) === key)
  )) || null;
}

export function emptyChiefComplaint(sequence = 0) {
  return {
    complaintCode: '',
    complaint: '',
    durationValue: '',
    durationUnit: 'Days',
    sequence,
  };
}

export function normalizeChiefComplaintRows(value, legacyProblem = '', options = {}) {
  const rows = Array.isArray(value) ? value : [];
  const preserveBlankRows = Boolean(options?.preserveBlankRows);
  const normalized = rows
    .map((row, index) => {
      if (typeof row === 'string') {
        const def = findComplaintDefinition(row);
        return {
          complaintCode: def?.code || '',
          complaint: def?.label || String(row || '').trim(),
          durationValue: '',
          durationUnit: 'Days',
          sequence: index,
        };
      }
      const complaint = String(row?.complaint || row?.label || row?.presentingProblem || '').trim();
      const def = findComplaintDefinition(row?.complaintCode || complaint);
      return {
        complaintCode: String(row?.complaintCode || def?.code || '').trim(),
        complaint: def?.label || complaint,
        durationValue: row?.durationValue ?? row?.duration ?? '',
        durationUnit: DURATION_UNITS.includes(row?.durationUnit) ? row.durationUnit : 'Days',
        sequence: Number.isFinite(Number(row?.sequence)) ? Number(row.sequence) : index,
      };
    })
    .filter((row) => preserveBlankRows || row.complaint);

  const hasMeaningfulComplaint = normalized.some((row) => row.complaint);
  if (!hasMeaningfulComplaint && legacyProblem) {
    const def = findComplaintDefinition(legacyProblem);
    const legacyRow = {
      complaintCode: def?.code || '',
      complaint: def?.label || String(legacyProblem).trim(),
      durationValue: '',
      durationUnit: 'Days',
      sequence: 0,
    };

    if (preserveBlankRows && normalized.length) normalized[0] = legacyRow;
    else normalized.push(legacyRow);
  }

  return normalized.map((row, index) => ({ ...row, sequence: index }));
}

export function isMeaningfulChiefComplaint(row) {
  return Boolean(String(row?.complaint || '').trim());
}
